/**
 * Thin fetch wrapper for `/api`. Same-origin cookies, JSON in and out,
 * errors as `ApiError` with the server's status and body.
 */
export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${String(status)}`);
    this.name = 'ApiError';
  }
}

/** Characters, below the browsers' 64 KB keepalive limit (UTF-8 may need more bytes). */
const KEEPALIVE_MAX = 30_000;

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; keepalive?: boolean } = {},
): Promise<T> {
  const request: RequestInit = { method: init.method ?? 'GET', credentials: 'same-origin' };
  if (init.body !== undefined) {
    request.headers = { 'content-type': 'application/json' };
    request.body = JSON.stringify(init.body);
  }
  // keepalive: the request survives the page being closed (last-moment
  // saves). Browsers refuse keepalive bodies over 64 KB outright; a bigger
  // one goes as a normal request, which may still make it.
  if (
    init.keepalive === true &&
    (typeof request.body !== 'string' || request.body.length < KEEPALIVE_MAX)
  ) {
    request.keepalive = true;
  }
  const res = await fetch(path, request);
  const text = await res.text();
  const body: unknown = text === '' ? null : JSON.parse(text);
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}
